import type { RootState } from '@/app/store';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { Volume2, PictureInPicture2, Maximize2, Hourglass } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
} from 'react';
import { useWebinarT } from '@/hooks/useWebinarT';
import { useWebinarLng } from '@/contexts/WebinarLanguageContext';
import { getAppLanguage } from '@/i18n/languages';
import { useParams, useSearchParams } from 'react-router-dom';
import Hls from 'hls.js';
import Bugsnag from '@bugsnag/js';
import {
  fetchPublicOffer,
  postAnalyticsEvent,
  fetchPublicResourceState,
  fetchPublicResource,
} from '@/features/live/liveThunks';
import {
  clearPublicOffer,
  clearPublicSurvey,
  clearPublicQuiz,
  clearPublicResource,
} from '@/features/live/liveSlice';
import { applySharedResourceLiveEvent } from '@/features/webinar/webinarSlice';
import type { SharedResourcePublic } from '@/features/webinar/types';
import type { PlaybackStreamQuality } from '@/features/live/types';
import { type VideoViewHandle } from '@/components/common/asset/VideoView';
import { useChannel } from 'ably/react';
import { useChannelName } from '@/routes/AblyAuth';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { resolveLivePlaybackUrl, selectStream, type ConnectionTier } from '@/lib/streamSelection';
import {
  BANDWIDTH_MIN_SAMPLES,
  compareTiers,
  measureConnectionTier,
  tierFromBandwidth,
} from '@/lib/connectionQuality';
import { resourceStateRefetchDelayMs } from '@/lib/liveEventBatch';
import {
  isLivePlaybackStuck,
  LIVE_RESUME_PROGRESS_CHECK_MS,
  needsLiveResync,
} from '@/lib/liveResume';
import {
  categorizeHlsError,
  createStallState,
  hlsErrorSeen,
  stallBegan,
  stallCancelled,
  stallEnded,
  stallInterrupted,
  stallSeeked,
  stallSummary,
  type StallState,
} from '@/lib/playbackStallTracker';
import {
  createDowngradeState,
  evaluateDowngrade,
  recordStall,
  recordStreamDead,
  type DowngradeState,
} from '@/lib/streamDowngrade';
import {
  loadDowngradeState,
  saveDowngradeState,
  sessionStorageOrNull,
} from '@/lib/streamDowngradeStorage';
import {
  enterPictureInPicture,
  exitPictureInPicture,
  exitVideoFullscreen,
  isAppleMobile,
  isInPictureInPicture,
  isMobileViewer,
  isVideoPlaying,
  requestVideoFullscreen,
  supportsPictureInPicture,
} from '@/lib/videoPresentation';

function canPlayNativeHls(video: HTMLVideoElement): boolean {
  return (
    !!video.canPlayType('application/vnd.apple.mpegurl') ||
    !!video.canPlayType('application/x-mpegURL')
  );
}

function isSafariDesktop(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /Macintosh/i.test(ua) &&
    /Safari/i.test(ua) &&
    !/Chrom(e|ium)|Edg|OPR|Brave|Firefox|FxiOS|CriOS/i.test(ua)
  );
}

function shouldUseNativeHls(video: HTMLVideoElement): boolean {
  if (typeof navigator === 'undefined') return false;
  // Always native HLS on Apple mobile.
  if (isAppleMobile()) return true;
  if (!canPlayNativeHls(video)) return false;
  return isSafariDesktop();
}

const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const isBugsnagEnabled = bugsnagApiKey !== undefined && bugsnagApiKey !== '';

/** Message volontairement fixe : Bugsnag regroupe alors toutes les sessions en une seule issue. */
const STALL_REPORT_MESSAGE = 'Attendee playback stalled';

/** Garde entre deux changements de palier : chacun ré-attache le lecteur et repart d'une estimation vierge. */
const TIER_CHANGE_COOLDOWN_MS = 60_000;

const reportStalls = (metadata: Record<string, unknown>): void => {
  if (!isBugsnagEnabled) return;
  Bugsnag.notify(new Error(STALL_REPORT_MESSAGE), (event) => {
    event.severity = 'warning';
    event.addMetadata('playbackStalls', metadata);
  });
};

/** Le segment de qualité de l'URL de flux (`desktop`, `p720`, …), sans embarquer l'URL elle-même. */
function streamQualityFromUrl(url: string): string {
  const match = /\/(desktop|mobile|p\d{3,4})\//.exec(url);
  return match ? match[1] : 'unknown';
}

function getWallClockMsFromVideo(video: HTMLVideoElement): number | null {
  const v = video as HTMLVideoElement & { getStartDate?: () => Date };
  if (typeof v.getStartDate !== 'function') return null;
  try {
    const start = v.getStartDate();
    if (!start || Number.isNaN(start.getTime())) return null;
    return start.getTime() + video.currentTime * 1000;
  } catch {
    return null;
  }
}

function seekVideoToLiveEdge(video: HTMLVideoElement) {
  try {
    const seekable = video.seekable;
    if (seekable.length > 0) {
      const end = seekable.end(seekable.length - 1);
      if (Number.isFinite(end)) {
        video.currentTime = Math.max(0, end - 3);
      }
    }
  } catch {
    /* seek refusé tant que le buffer n’est pas prêt */
  }
}

export type VideoHlsViewHandle = {
  target: 'mobile' | 'tablet' | 'desktop';
  handleResourceEvent: (event: {
    eventType: 'RESOURCE_SHOW' | 'RESOURCE_HIDE';
    resourceType: string;
    resourceId: number;
    timeNow: number;
    authorization: string;
    /** Ressources partagées uniquement : pas d'endpoint public par id, le DTO complet transite
     * directement dans l'event plutôt que via un fetch une fois le délai de sync écoulé. */
    resource?: SharedResourcePublic;
  }) => void;
  handleSurveyResultsEvent: (event: {
    eventType: 'SHOW_SURVEY_RESPONSE' | 'HIDE_SURVEY_RESPONSE';
    templateId: number;
    timeNow: number;
    onUpdate: (templateId: number, show: boolean) => void;
  }) => void;
  handleEndSessionEvent: (event: { timeNow: number; onNavigate: () => void }) => void;
  getCurrentTimestamp: () => number | null;
};

export default forwardRef<
  VideoHlsViewHandle,
  {
    target: 'mobile' | 'desktop';
    visualIdentity: WorkspaceVisualIdentity;
    interactionVisible: boolean;
  }
>(function VideoHlsView({ target, visualIdentity, interactionVisible }, ref) {
  const { t, i18n } = useWebinarT('common');
  const lng = useWebinarLng() ?? i18n.language;
  const { t: tWebinars } = useWebinarT('webinars');
  const playbackInfo = useAppSelector((s: RootState) => s.live.playbackInfo);
  const [connectionTier, setConnectionTier] = useState<ConnectionTier>('high');
  // Collant : une capacité mesurée reste vraie, même quand un changement de flux remet le compteur
  // de segments à zéro et renvoie la mesure au ping le temps d'en recharger trois.
  const [connectionMeasured, setConnectionMeasured] = useState(false);
  // Pixels physiques du cadre vidéo. La densité est plafonnée à 2 : au-delà l'œil ne suit plus et
  // on ne ferait qu'acheter des octets. Voir `framePixels` dans streamSelection.
  const [framePixels, setFramePixels] = useState(() =>
    typeof window === 'undefined'
      ? 1920
      : Math.round(window.innerWidth * Math.min(window.devicePixelRatio || 1, 2)),
  );
  const measureFrame = useCallback((cssWidth: number) => {
    const ratio = Math.min(typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1, 2);
    const next = Math.round(cssWidth * ratio);
    if (next > 0) setFramePixels(next);
  }, []);
  /** Dernier palier appliqué, lu hors rendu par la boucle de mesure. */
  const connectionTierRef = useRef<ConnectionTier>('high');
  /** Une amélioration doit se confirmer sur deux mesures : changer de palier ré-attache le lecteur. */
  const pendingTierUpgradeRef = useRef<ConnectionTier | null>(null);
  const lastTierChangeAtRef = useRef(0);
  useEffect(() => {
    let cancelled = false;
    const applyTier = (next: ConnectionTier | null) => {
      if (cancelled || next === null) return;
      // Changer de palier change l'URL et ré-attache le lecteur, ce qui remet l'estimation de débit
      // à zéro. Sans garde, ce cycle s'auto-entretient et le spectateur voit la vidéo se recharger.
      if (Date.now() - lastTierChangeAtRef.current < TIER_CHANGE_COOLDOWN_MS) return;
      // Descendre protège le spectateur : on le fait sans attendre de confirmation.
      if (compareTiers(next, connectionTierRef.current) <= 0) {
        pendingTierUpgradeRef.current = null;
        connectionTierRef.current = next;
        lastTierChangeAtRef.current = Date.now();
        setConnectionTier(next);
        return;
      }
      if (pendingTierUpgradeRef.current !== next) {
        pendingTierUpgradeRef.current = next;
        return;
      }
      pendingTierUpgradeRef.current = null;
      connectionTierRef.current = next;
      lastTierChangeAtRef.current = Date.now();
      setConnectionTier(next);
    };
    const measure = () => {
      // Débit réellement observé sur les segments, quand hls.js en a assez vu pour l'estimer.
      const estimate = hlsRef.current?.bandwidthEstimate ?? 0;
      if (loadedFragmentsRef.current >= BANDWIDTH_MIN_SAMPLES && estimate > 0) {
        if (!cancelled) setConnectionMeasured(true);
        applyTier(tierFromBandwidth(estimate));
        return;
      }
      // Repli : lecture native d'Apple, ou tout début de lecture.
      void measureConnectionTier().then(applyTier);
    };
    measure();
    // Lire l'estimation ne coûte plus aucune requête : on peut mesurer souvent, ce qui permet de
    // descendre AVANT que le tampon ne se vide plutôt que de réparer un gel déjà subi.
    const intervalId = window.setInterval(measure, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);
  // La largeur est ensuite reprise par l'observateur du cadre vidéo ; ce repli ne sert qu'avant
  // son montage, et quand `ResizeObserver` n'est pas disponible.
  useEffect(() => {
    const onResize = () => {
      if (videoHlsRef.current === null) measureFrame(window.innerWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measureFrame]);
  // Repli automatique : le back met ~30 s à voir qu'un flux ne progresse plus, et le spectateur
  // doit encore refetch pour l'apprendre. Le gel vécu tranche en 8 s, donc il reste le seul moyen
  // de quitter tout de suite un flux devenu inexploitable.
  const { sessionIdentifier } = useParams<{ sessionIdentifier: string }>();
  // Relu au montage : le spectateur recharge face à un gel, et sans mémoire il retomberait sur le
  // flux qui vient de geler. Lu dès le premier rendu pour ne pas démarrer sur ce flux-là.
  const [restoredDowngrade] = useState(
    () =>
      loadDowngradeState(sessionStorageOrNull(), sessionIdentifier, Date.now()) ??
      createDowngradeState(),
  );
  const downgradeStateRef = useRef<DowngradeState>(restoredDowngrade);
  const [avoidedQualities, setAvoidedQualities] = useState<PlaybackStreamQuality[]>(
    restoredDowngrade.avoided,
  );
  const [bandwidthCeiling, setBandwidthCeiling] = useState<PlaybackStreamQuality | null>(
    restoredDowngrade.bandwidthCeiling,
  );
  /** Qualité réellement jouée, lue hors rendu par les décisions de repli. */
  const currentQualityRef = useRef<PlaybackStreamQuality | null>(null);
  const sessionIdentifierRef = useRef(sessionIdentifier);
  useEffect(() => {
    sessionIdentifierRef.current = sessionIdentifier;
  }, [sessionIdentifier]);
  const lastSavedDowngradeRef = useRef('');
  /** `force` rafraîchit l'horodatage au départ : l'expiration court à partir du dernier passage. */
  const persistDowngrade = useCallback((nowMs: number, force = false) => {
    const snapshot = JSON.stringify(downgradeStateRef.current);
    if (!force && snapshot === lastSavedDowngradeRef.current) return;
    lastSavedDowngradeRef.current = snapshot;
    saveDowngradeState(
      sessionStorageOrNull(),
      sessionIdentifierRef.current,
      downgradeStateRef.current,
      nowMs,
    );
  }, []);
  const applyDowngradeDecision = useCallback(
    (nowMs: number) => {
      const next = evaluateDowngrade(downgradeStateRef.current, nowMs, {
        currentQuality: currentQualityRef.current,
        pendingStallSinceMs: stallStateRef.current.pending?.startedAtMs ?? null,
        bandwidthBps:
          loadedFragmentsRef.current >= BANDWIDTH_MIN_SAMPLES
            ? (hlsRef.current?.bandwidthEstimate ?? null)
            : null,
      });
      downgradeStateRef.current = next;
      setAvoidedQualities((current) =>
        current.length === next.avoided.length && current.every((q, i) => q === next.avoided[i])
          ? current
          : next.avoided,
      );
      setBandwidthCeiling(next.bandwidthCeiling);
      persistDowngrade(nowMs);
    },
    [persistDowngrade],
  );
  /**
   * Départ de la page ou onglet masqué pendant un gel : c'est souvent le geste qui suit un gel. Le
   * gel est clos à cet instant, compté dans le rapport comme dans le repli, puis l'état est conservé
   * pour le rechargement. Idempotent : `pagehide` et `visibilitychange` arrivent dans un ordre variable.
   */
  const settleOnLeave = useCallback(() => {
    const nowMs = Date.now();
    const pending = stallStateRef.current.pending;
    if (pending !== null) {
      stallStateRef.current = stallInterrupted(stallStateRef.current, nowMs);
      downgradeStateRef.current = recordStall(
        downgradeStateRef.current,
        nowMs,
        nowMs - pending.startedAtMs,
      );
      applyDowngradeDecision(nowMs);
    }
    persistDowngrade(nowMs, true);
  }, [applyDowngradeDecision, persistDowngrade]);
  useEffect(() => {
    // Battement court : c'est lui qui voit un gel encore EN COURS, cas d'un flux définitivement
    // coupé qui ne produira jamais de gel terminé. Il porte aussi la remontée quand tout va bien.
    const intervalId = window.setInterval(() => applyDowngradeDecision(Date.now()), 2000);
    return () => window.clearInterval(intervalId);
  }, [applyDowngradeDecision]);
  const playbackUrl = useMemo(() => {
    const selected = selectStream(playbackInfo?.streams, connectionTier, {
      framePixels,
      avoidedQualities,
      connectionMeasured,
      bandwidthCeiling,
    });
    currentQualityRef.current = selected?.quality ?? null;
    return resolveLivePlaybackUrl(
      playbackInfo?.streams,
      selected,
      target ? playbackInfo?.[target]?.hlsLiveUrl : playbackInfo?.hlsLiveUrl,
    );
  }, [
    playbackInfo?.streams,
    connectionTier,
    framePixels,
    avoidedQualities,
    connectionMeasured,
    bandwidthCeiling,
    target,
    playbackInfo?.[target]?.hlsLiveUrl,
    playbackInfo?.hlsLiveUrl,
  ]);
  const [hlsSupported, setHlsSupported] = useState(true);
  const [liveStreamManifestReady, setLiveStreamManifestReady] = useState(false);
  // Distingue l'attente d'avant-live (« va bientôt commencer ») d'une coupure en cours de live
  // (« on se retrouve dans un instant ») : survit aux cycles de ré-attache du player.
  const hasStreamPlayedRef = useRef(false);
  const videoHlsRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  /** hls.js sème son estimation avec une valeur par défaut : sans segments, elle ne vaut rien. */
  const loadedFragmentsRef = useRef(0);
  const nativeHlsSeekSafeAfterRef = useRef(0);
  const userPrefersUnmutedRef = useRef(false);
  const [isMuted, setIsMuted] = useState(true);
  const [canUsePip, setCanUsePip] = useState(false);
  const [isInPip, setIsInPip] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const hideControlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentVideoPlaybackTimestamp, setCurrentVideoPlaybackTimestamp] = useState<number | null>(
    null,
  );
  /** Dernière valeur à jour pour l’API impérative ; l’état React est limité pour ne pas sur-rendre la page. */
  const playbackTimestampRef = useRef<number | null>(null);
  const lastEmittedTsForQueueRef = useRef(0);
  /** Survit aux ré-attaches du player : une bascule de qualité ne doit pas remettre le compteur à zéro. */
  const stallStateRef = useRef<StallState>(createStallState());
  /** Lu à l'envoi, qui se produit hors rendu : sans ref on figerait le contexte du premier montage. */
  const streamContextRef = useRef({ quality: '', connectionTier });
  streamContextRef.current = { quality: streamQualityFromUrl(playbackUrl), connectionTier };

  const emitPlaybackTimestampThrottled = (next: number | null) => {
    playbackTimestampRef.current = next;
    if (next == null) {
      lastEmittedTsForQueueRef.current = 0;
      setCurrentVideoPlaybackTimestamp(null);
      return;
    }
    const now = Date.now();
    if (now - lastEmittedTsForQueueRef.current < 150) return;
    lastEmittedTsForQueueRef.current = now;
    setCurrentVideoPlaybackTimestamp(next);
  };

  const [timeBasedEvent, setTimeBasedEvent] = useState<
    {
      time: number | null;
      action: () => void;
      label: string;
    }[]
  >([]);

  const dispatch = useAppDispatch();
  const pendingResourceStateRefetchIds = useRef<Set<number>>(new Set());
  useEffect(
    () => () => {
      pendingResourceStateRefetchIds.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      pendingResourceStateRefetchIds.current.clear();
    },
    [],
  );
  const webinarId = useAppSelector((s: RootState) => s.live.publicSession?.session?.webinarId);
  const sessionStatus = useAppSelector((s: RootState) => s.live.publicSession?.session?.status);
  const backgroundColor = useAppSelector(
    (s: RootState) => s.live.publicSession?.visualIdentity?.backgroundColor,
  );
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? undefined;

  const publicResource = useAppSelector((s: RootState) => s.live.publicResource);
  const hasPlaybackUrlForTarget = !!playbackUrl;

  const handleGoLive = () => {
    const video = videoHlsRef.current;
    const hls = hlsRef.current;
    if (!video) return;
    if (hls && typeof hls.liveSyncPosition === 'number' && hls.liveSyncPosition > 0) {
      video.currentTime = hls.liveSyncPosition;
    } else {
      seekVideoToLiveEdge(video);
    }
    void video.play();
  };

  /**
   * Reprise du live après sortie de PiP / plein écran.
   * iOS (HLS natif) met la vidéo en pause à la sortie et n'autorise `play()` que dans le geste :
   * on relance tout de suite, en place (avec un filet anti-pause). Sur hls.js (desktop) on resynchronise
   * en plus au vrai bord du live (`seekable.end`), `liveSyncPosition` étant périmé après la pause.
   */
  const recoverToLiveAfterExit = () => {
    const video = videoHlsRef.current;
    const hls = hlsRef.current;
    if (!video) return;

    // Relance immédiate, dans le geste de sortie (sinon iOS rejette le play()).
    void video.play().catch(() => {});

    // Filet : si elle retombe en pause juste après (iOS), on retente la lecture quelques secondes.
    let retries = 0;
    const onUnexpectedPause = () => {
      if (retries >= 5) return;
      retries += 1;
      void video.play().catch(() => {});
    };
    video.addEventListener('pause', onUnexpectedPause);
    setTimeout(() => video.removeEventListener('pause', onUnexpectedPause), 4000);

    // hls.js (desktop) : resync au vrai bord du live, liveSyncPosition étant périmé après la pause.
    if (hls) {
      requestAnimationFrame(() => {
        try {
          hls.startLoad();
        } catch {
          /* ignore */
        }
        const seekable = video.seekable;
        const end = seekable.length ? seekable.end(seekable.length - 1) : null;
        if (end != null && Number.isFinite(end)) {
          try {
            video.currentTime = Math.max(0, end - 3);
          } catch {
            /* seek refusé tant que le buffer n'est pas prêt */
          }
        } else if (typeof hls.liveSyncPosition === 'number' && hls.liveSyncPosition > 0) {
          video.currentTime = hls.liveSyncPosition;
        }
        void video.play().catch(() => {});
      });
    }
  };

  const toggleMute = () => {
    const video = videoHlsRef.current;
    if (video) {
      video.muted = !video.muted;
      userPrefersUnmutedRef.current = !video.muted;
      setIsMuted(video.muted);
    }
  };

  /** Bascule la mini-fenêtre via un geste utilisateur (seul moyen fiable d'entrer en PiP sur Android). */
  const togglePictureInPicture = () => {
    const video = videoHlsRef.current;
    if (!video) return;
    if (isInPictureInPicture(video)) {
      void exitPictureInPicture(video);
    } else {
      void enterPictureInPicture(video);
    }
  };

  /** Plein écran natif, prérequis pour l'auto-PiP. Bloqué tant qu'une offre est à l'écran. */
  const enterFullscreen = () => {
    if (interactionVisible) return;
    const video = videoHlsRef.current;
    if (video) requestVideoFullscreen(video);
  };

  /** Une offre / sondage / quizz apparaît : on sort du plein écran pour qu'elle reste visible. */
  useEffect(() => {
    if (!interactionVisible) return;
    const video = videoHlsRef.current;
    if (video) exitVideoFullscreen(video);
  }, [interactionVisible]);

  /**
   * Sortie de plein écran : sur iOS (HLS natif) la vidéo se met en pause, et le seul rattrapage
   * est l'intervalle `lag > 20` (~20 s de gel). On relance immédiatement au bord du live.
   */
  useEffect(() => {
    const video = videoHlsRef.current;
    if (!video) return;

    const onFullscreenChange = () => {
      if (typeof document !== 'undefined' && !document.fullscreenElement) recoverToLiveAfterExit();
    };
    const onWebkitEndFullscreen = () => recoverToLiveAfterExit();

    document.addEventListener('fullscreenchange', onFullscreenChange);
    video.addEventListener('webkitendfullscreen', onWebkitEndFullscreen);

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      video.removeEventListener('webkitendfullscreen', onWebkitEndFullscreen);
    };
  }, [hlsSupported]);

  // La largeur qui compte est celle du cadre vidéo, pas celle de la fenêtre : sur desktop le player
  // n'occupe qu'une partie de la page, et tirer du 1080p pour l'afficher en 700 px est du gâchis.
  useEffect(() => {
    const video = videoHlsRef.current;
    if (!video || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      measureFrame(entry.contentRect.width);
    });
    observer.observe(video);
    return () => observer.disconnect();
  }, [hlsSupported, playbackUrl, measureFrame]);

  /** Affiche les contrôles au tap puis les masque seuls (player classique, pas de boutons permanents). */
  const revealControls = () => {
    setShowControls(true);
    if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    hideControlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
  };

  /** Desktop (souris) : on révèle les contrôles au survol ; le tactile reste au tap. */
  const canHoverRef = useRef(
    typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches,
  );
  const showControlsOnHover = () => {
    if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    setShowControls(true);
  };
  const hideControlsOnHover = () => {
    if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    setShowControls(false);
  };

  useEffect(() => {
    return () => {
      if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    };
  }, []);

  /** Reprise après mise en arrière-plan / verrouillage (mobile) : MSE et la balise video se figent souvent sans ça. */
  useEffect(() => {
    let progressCheckId: number | null = null;

    const resyncLivePlayback = () => {
      const video = videoHlsRef.current;
      const hls = hlsRef.current;
      if (!video) return;

      if (hls) {
        const sync = hls.liveSyncPosition;
        if (typeof sync === 'number' && Number.isFinite(sync) && sync > 0) {
          try {
            video.currentTime = sync;
          } catch {
            /* seek possiblement refusé tant que le pipeline n’est pas prêt */
          }
        }
        if (video.error) {
          try {
            hls.recoverMediaError();
          } catch {
            /* ignore */
          }
        }
      } else if (Date.now() >= nativeHlsSeekSafeAfterRef.current) {
        seekVideoToLiveEdge(video);
      }
      void video.play().catch(() => {});
    };

    const resumeLivePlayback = () => {
      const video = videoHlsRef.current;
      if (!video) return;

      const playback = {
        paused: video.paused,
        errored: video.error !== null,
        readyState: video.readyState,
      };
      if (needsLiveResync(playback)) {
        resyncLivePlayback();
        return;
      }

      // Elle semble jouer : on vérifie qu'elle avance avant de la déplacer.
      if (progressCheckId !== null) return;
      const startTimeS = video.currentTime;
      progressCheckId = window.setTimeout(() => {
        progressCheckId = null;
        const current = videoHlsRef.current;
        if (!current || document.visibilityState !== 'visible') return;
        if (isLivePlaybackStuck(startTimeS, current.currentTime)) resyncLivePlayback();
      }, LIVE_RESUME_PROGRESS_CHECK_MS);
    };

    const scheduleResume = () => {
      requestAnimationFrame(() => {
        resumeLivePlayback();
      });
    };

    const onVisibilityChange = () => {
      const video = videoHlsRef.current;
      if (!video) return;

      if (document.visibilityState === 'hidden') {
        // Changement d'app sur mobile : garder le live visible en mini-fenêtre plutôt que de couper.
        if (isMobileViewer() && isVideoPlaying(video)) {
          void enterPictureInPicture(video);
        }
        return;
      }

      // Retour sur Welya : on quitte la mini-fenêtre pour réafficher le live en grand, puis on relance.
      void exitPictureInPicture(video);
      scheduleResume();
    };

    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        scheduleResume();
      }
    };

    const onFocus = () => {
      scheduleResume();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);

    return () => {
      if (progressCheckId !== null) window.clearTimeout(progressCheckId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  /** Support de la mini-fenêtre (affichage du bouton) + synchro de l'état entrée/sortie PiP. */
  useEffect(() => {
    const video = videoHlsRef.current;
    if (!video) return;

    // Bouton PiP réservé au mobile : sur desktop on ne garde que le plein écran.
    setCanUsePip(isMobileViewer() && supportsPictureInPicture(video));
    setIsInPip(isInPictureInPicture(video));

    const onEnter = () => setIsInPip(true);
    // Fermer la mini-fenêtre met la vidéo en pause : on relance au bord du live au lieu d'attendre le rattrapage.
    const onLeave = () => {
      setIsInPip(false);
      recoverToLiveAfterExit();
    };
    const onWebkitChange = () => {
      const inPip = isInPictureInPicture(video);
      setIsInPip(inPip);
      if (!inPip) recoverToLiveAfterExit();
    };

    video.addEventListener('enterpictureinpicture', onEnter);
    video.addEventListener('leavepictureinpicture', onLeave);
    video.addEventListener('webkitpresentationmodechanged', onWebkitChange);

    return () => {
      video.removeEventListener('enterpictureinpicture', onEnter);
      video.removeEventListener('leavepictureinpicture', onLeave);
      video.removeEventListener('webkitpresentationmodechanged', onWebkitChange);
    };
  }, [hlsSupported]);

  // Détection des gels, branchée sur la balise vidéo et non sur un moteur : couvre donc aussi bien
  // hls.js que la lecture native de Safari et iOS.
  useEffect(() => {
    const video = videoHlsRef.current;
    if (!video) return;

    const onStalled = () => {
      // Une vidéo en pause ou un onglet masqué n'alimentent plus la lecture sans que rien n'aille mal.
      if (video.paused || document.visibilityState !== 'visible') return;
      stallStateRef.current = stallBegan(
        stallStateRef.current,
        Date.now(),
        playbackTimestampRef.current,
        streamContextRef.current.quality,
        video.currentTime,
      );
    };
    const settleStall = (mediaTimeS: number | null) => {
      const endedAtMs = Date.now();
      const pending = stallStateRef.current.pending;
      const next = stallEnded(stallStateRef.current, endedAtMs, mediaTimeS);
      if (next === stallStateRef.current) return;
      stallStateRef.current = next;
      if (pending === null) return;
      downgradeStateRef.current = recordStall(
        downgradeStateRef.current,
        endedAtMs,
        endedAtMs - pending.startedAtMs,
      );
      applyDowngradeDecision(endedAtMs);
    };
    const onPlaying = () => settleStall(null);
    // Seule une position qui avance vaut reprise : à l'arrêt, `timeupdate` continue d'arriver.
    const onTimeUpdate = () => settleStall(video.currentTime);
    const onInterrupted = () => {
      // iOS met la vidéo en pause en passant en arrière-plan : ce n'est pas une pause du spectateur.
      if (document.visibilityState !== 'visible') {
        settleOnLeave();
        return;
      }
      stallStateRef.current = stallCancelled(stallStateRef.current);
    };
    // En direct le spectateur ne peut pas chercher : un `seeking` est un saut du lecteur devant un
    // trou, et le gel continue. En replay c'est le spectateur, et le gel n'en est pas un.
    const onSeeking = () => {
      stallStateRef.current =
        sessionStatus === 'IN_PROGRESS'
          ? stallSeeked(stallStateRef.current, video.currentTime)
          : stallCancelled(stallStateRef.current);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') settleOnLeave();
    };

    video.addEventListener('waiting', onStalled);
    video.addEventListener('stalled', onStalled);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('pause', onInterrupted);
    video.addEventListener('seeking', onSeeking);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      video.removeEventListener('waiting', onStalled);
      video.removeEventListener('stalled', onStalled);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('pause', onInterrupted);
      video.removeEventListener('seeking', onSeeking);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      // Un gel en cours n'a plus de fin observable après la ré-attache : l'oublier plutôt que l'inventer.
      stallStateRef.current = stallCancelled(stallStateRef.current);
    };
  }, [playbackUrl, sessionStatus, applyDowngradeDecision, settleOnLeave]);

  // Envoi unique en fin de visionnage, et seulement s'il y a eu des gels : une session saine ne doit
  // rien émettre, sinon on produirait autant d'événements que de spectateurs.
  useEffect(() => {
    // Le compteur est cumulatif : ne renvoyer que s'il a bougé suffit à éviter les doublons, sans
    // interdire un second envoi au spectateur qui revient et regèle.
    let lastSentCount = 0;
    const send = () => {
      settleOnLeave();
      const summary = stallSummary(stallStateRef.current);
      if (summary === null || summary.count === lastSentCount) return;
      lastSentCount = summary.count;
      // `quality` reste le flux d'arrivée ; l'attribution réelle des gels vit dans `byQuality`.
      reportStalls({
        ...summary,
        ...streamContextRef.current,
        avoidedQualities: downgradeStateRef.current.avoided,
        bandwidthCeiling: downgradeStateRef.current.bandwidthCeiling,
        downgradeCount: downgradeStateRef.current.downgradeCount,
      });
    };
    // Le déchargement de page peut tuer la requête en vol, et un onglet mobile peut être supprimé
    // sans `pagehide` : le passage en arrière-plan est le dernier instant fiable pour émettre.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') send();
    };

    window.addEventListener('pagehide', send);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', send);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      send();
    };
  }, [settleOnLeave]);

  useEffect(() => {
    const video = videoHlsRef.current;
    if (!video) return;

    let cancelled = false;

    const notifyReady = (ready: boolean) => {
      if (!cancelled) {
        if (ready) hasStreamPlayedRef.current = true;
        setLiveStreamManifestReady(ready);
      }
    };

    const applyUserMutePreference = () => {
      const muted = !userPrefersUnmutedRef.current;
      video.muted = muted;
      setIsMuted(muted);
    };
    applyUserMutePreference();

    if (!playbackUrl) {
      notifyReady(false);
      return () => {
        cancelled = true;
      };
    }

    if (sessionStatus === 'FINISHED') {
      notifyReady(false);
      return () => {
        cancelled = true;
      };
    }

    const useNativePlayback = shouldUseNativeHls(video);
    const useMseHls = Hls.isSupported();
    if (!useNativePlayback && !useMseHls) {
      setHlsSupported(false);
      return () => {
        cancelled = true;
      };
    }
    setHlsSupported(true);

    let retryManifestIntervalId: ReturnType<typeof setInterval> | null = null;
    let cleanupFunction: (() => void) | undefined;

    const clearManifestRetry = () => {
      if (retryManifestIntervalId != null) {
        clearInterval(retryManifestIntervalId);
        retryManifestIntervalId = null;
      }
    };

    let startPlaybackAfterManifestReady: () => void;

    const scheduleManifestRetry = () => {
      if (retryManifestIntervalId != null) return;
      retryManifestIntervalId = setInterval(() => {
        if (cancelled) {
          clearManifestRetry();
          return;
        }
        cleanupFunction?.();
        cleanupFunction = undefined;
        startPlaybackAfterManifestReady();
      }, 2000);
    };

    const attachMseHls = (): (() => void) => {
      notifyReady(false);

      const hls = new Hls({
        lowLatencyMode: false,
        // Calé sur des segments de 4 s et une fenêtre de 20 s côté egress : 3 segments de réserve
        // devant, 2 de marge derrière. Ne pas relever sans que la fenêtre ait suivi.
        liveSyncDuration: 12,
        // Au-delà de 18 s de retard, hls.js resynchronise d'un coup à 12 s du bord : le spectateur
        // perd 6 s de contenu, mais ne sort pas de la fenêtre. Hors lowLatencyMode il n'existe
        // aucun rattrapage progressif, maxLiveSyncPlaybackRate y est ignoré.
        liveMaxLatencyDuration: 18,
        maxLiveSyncPlaybackRate: 1,
        backBufferLength: 30,
        maxBufferLength: 60,
        maxMaxBufferLength: 120,
        capLevelToPlayerSize: false,
        startLevel: -1,
      });
      hlsRef.current = hls;

      hls.loadSource(playbackUrl);
      hls.attachMedia(video);

      const onManifestParsed = function () {
        if (video) {
          video.currentTime = hls.liveSyncPosition || video.duration;
          void video.play().catch(() => {});
        }
      };

      // Le « prêt » se décide au chargement du level : une playlist VOD (#EXT-X-ENDLIST) alors que
      // la session est en direct est un flux mort (egress tué) → on garde l'overlay et on re-sonde
      // jusqu'à ce qu'un egress relancé réécrive une playlist live.
      const onLevelLoaded = (_evt: string, data: { details?: { live?: boolean } }) => {
        if (sessionStatus === 'IN_PROGRESS' && data?.details?.live === false) {
          // Playlist repassée en VOD en plein direct : l'egress est mort. La pause qui suit rendrait
          // le gel invisible, donc on rend le verdict ici, avant qu'il n'y ait plus rien à observer.
          const deadAtMs = Date.now();
          downgradeStateRef.current = recordStreamDead(downgradeStateRef.current, deadAtMs);
          applyDowngradeDecision(deadAtMs);
          video.pause();
          notifyReady(false);
          scheduleManifestRetry();
          return;
        }
        clearManifestRetry();
        notifyReady(true);
      };

      const onFragLoaded = () => {
        loadedFragmentsRef.current += 1;
      };

      const onHlsError = (
        _evt: string,
        data: { fatal?: boolean; type?: string; details?: string },
      ) => {
        // Les non fatales sont réessayées seules par hls.js : c'est précisément là que vivent les gels.
        if (!data.fatal) {
          stallStateRef.current = hlsErrorSeen(
            stallStateRef.current,
            categorizeHlsError(data.details),
          );
        }
        if (data.fatal) {
          // Verdict sans appel, mais seulement sur un flux qui a déjà joué : au démarrage les egress
          // ne sont pas encore là, et un 404 d'amorçage ne dit rien de la qualité de la connexion.
          if (hasStreamPlayedRef.current || loadedFragmentsRef.current > 0) {
            const deadAtMs = Date.now();
            downgradeStateRef.current = recordStreamDead(downgradeStateRef.current, deadAtMs);
            applyDowngradeDecision(deadAtMs);
          }
          notifyReady(false);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            scheduleManifestRetry();
          }
        }
      };

      const onTimeUpdateHls = () => {
        const hlsAny = hls as unknown as {
          streamController?: {
            media?: { currentFrag?: { programDateTime: number; start: number } };
            currentFrag?: { programDateTime: number; start: number };
          };
        };
        const currentFrag =
          hlsAny?.streamController?.media?.currentFrag || hlsAny?.streamController?.currentFrag;

        if (currentFrag) {
          emitPlaybackTimestampThrottled(
            new Date(
              currentFrag.programDateTime + (video.currentTime - currentFrag.start) * 1000,
            ).getTime(),
          );
        }
      };

      loadedFragmentsRef.current = 0;
      hls.on(Hls.Events.FRAG_LOADED, onFragLoaded);
      hls.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);
      hls.on(Hls.Events.LEVEL_LOADED, onLevelLoaded);
      hls.on(Hls.Events.ERROR, onHlsError);
      video.addEventListener('timeupdate', onTimeUpdateHls);

      return () => {
        video.removeEventListener('timeupdate', onTimeUpdateHls);
        hls.destroy();
        hlsRef.current = null;
      };
    };

    const attachNativeHls = (): (() => void) => {
      notifyReady(false);
      hlsRef.current = null;
      nativeHlsSeekSafeAfterRef.current = Date.now() + 4000;

      let readyNotified = false;
      const markNativeReadyOnce = () => {
        if (cancelled || readyNotified) return;
        readyNotified = true;
        clearManifestRetry();
        notifyReady(true);
        void video.play().catch(() => {});
      };

      const onError = () => {
        if (cancelled) return;
        notifyReady(false);
        const code = video.error?.code;
        if (
          code === MediaError.MEDIA_ERR_NETWORK ||
          code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
        ) {
          scheduleManifestRetry();
        }
      };

      const onTimeUpdateNative = () => {
        if (cancelled) return;
        if (!readyNotified && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          markNativeReadyOnce();
        }
        const wall = getWallClockMsFromVideo(video);
        if (wall !== null) {
          emitPlaybackTimestampThrottled(wall);
        }
      };

      // Un flux qui se termine alors que la session est en direct = playlist clôturée par un
      // egress tué : on repasse en attente et on re-sonde jusqu'à la playlist réécrite.
      const onEndedWhileLive = () => {
        if (cancelled || sessionStatus !== 'IN_PROGRESS') return;
        readyNotified = false;
        notifyReady(false);
        scheduleManifestRetry();
      };

      video.src = playbackUrl;

      video.addEventListener('canplay', markNativeReadyOnce);
      video.addEventListener('loadeddata', markNativeReadyOnce);
      video.addEventListener('playing', markNativeReadyOnce);
      video.addEventListener('ended', onEndedWhileLive);
      video.addEventListener('error', onError);
      video.addEventListener('timeupdate', onTimeUpdateNative);

      const liveCheckInterval = setInterval(() => {
        if (cancelled || !video.seekable?.length) return;
        const seekable = video.seekable;
        const end = seekable.end(seekable.length - 1);
        if (!Number.isFinite(end)) return;
        const lag = end - video.currentTime;
        if (lag > 20) handleGoLive();
      }, 5000);

      return () => {
        clearInterval(liveCheckInterval);
        video.removeEventListener('canplay', markNativeReadyOnce);
        video.removeEventListener('loadeddata', markNativeReadyOnce);
        video.removeEventListener('playing', markNativeReadyOnce);
        video.removeEventListener('ended', onEndedWhileLive);
        video.removeEventListener('error', onError);
        video.removeEventListener('timeupdate', onTimeUpdateNative);
        video.removeAttribute('src');
        video.load();
        hlsRef.current = null;
      };
    };

    startPlaybackAfterManifestReady = () => {
      if (useNativePlayback) {
        cleanupFunction = attachNativeHls();
      } else {
        const teardownMse = attachMseHls();
        const liveCheckInterval = setInterval(() => {
          const hls = hlsRef.current;
          if (video && hls?.liveSyncPosition) {
            const lag = hls.liveSyncPosition - video.currentTime;
            if (lag > 20) handleGoLive();
          }
        }, 5000);
        cleanupFunction = () => {
          clearInterval(liveCheckInterval);
          teardownMse();
        };
      }
    };

    startPlaybackAfterManifestReady();

    return () => {
      cancelled = true;
      clearManifestRetry();
      cleanupFunction?.();
      setLiveStreamManifestReady(false);
    };
  }, [playbackUrl, sessionStatus, applyDowngradeDecision]);

  useEffect(() => {
    const actionsToRun = timeBasedEvent.filter(
      (event) =>
        event.time && currentVideoPlaybackTimestamp && event.time < currentVideoPlaybackTimestamp,
    );
    const stillPending = timeBasedEvent.filter((event) => !actionsToRun.includes(event));

    if (timeBasedEvent.length > 0 || actionsToRun.length > 0) {
      const videoTimeStr = currentVideoPlaybackTimestamp
        ? new Date(currentVideoPlaybackTimestamp).toISOString()
        : '—';
      console.log('[VideoHlsView] Événements en file', {
        currentVideoPlaybackTimestamp: videoTimeStr,
        pending: stillPending.map((e) => ({
          label: e.label,
          time: e.time ? new Date(e.time).toISOString() : null,
          deltaSec:
            e.time && currentVideoPlaybackTimestamp
              ? (e.time - currentVideoPlaybackTimestamp) / 1000
              : null,
        })),
        executing: actionsToRun.map((e) => ({
          label: e.label,
          time: e.time ? new Date(e.time).toISOString() : null,
        })),
      });
    }

    actionsToRun.forEach((action) => action.action());

    setTimeBasedEvent((prev) => prev.filter((event) => !actionsToRun.includes(event)));
  }, [currentVideoPlaybackTimestamp]);

  // Le refetch est étalé sur une fenêtre aléatoire : sans ça, tous les spectateurs tapent
  // `/resources/state` à la milliseconde près sur le même event.
  const scheduleResourceStateRefetch = (
    targetWebinarId: number,
    targetSessionIdentifier: string,
    authorization: string,
  ) => {
    const timeoutId = window.setTimeout(() => {
      pendingResourceStateRefetchIds.current.delete(timeoutId);
      dispatch(
        fetchPublicResourceState({
          webinarId: targetWebinarId,
          sessionIdentifier: targetSessionIdentifier,
          authorization,
          coalesce: true,
        }),
      );
    }, resourceStateRefetchDelayMs());
    pendingResourceStateRefetchIds.current.add(timeoutId);
  };

  const handleResourceEvent = ({
    eventType,
    resourceType,
    resourceId,
    timeNow,
    authorization,
    resource,
  }: {
    eventType: 'RESOURCE_SHOW' | 'RESOURCE_HIDE';
    resourceType: string;
    resourceId: number;
    timeNow: number;
    authorization: string;
    resource?: SharedResourcePublic;
  }) => {
    console.log('handleResourceEvent', { eventType, resourceType, resourceId, timeNow });

    if (
      (resourceType !== 'OFFER_TEMPLATE' &&
        resourceType !== 'SURVEY_TEMPLATE' &&
        resourceType !== 'QUIZ_TEMPLATE' &&
        resourceType !== 'SHARED_RESOURCE') ||
      !webinarId ||
      !authorization
    ) {
      return;
    }

    const runEventAction = () => {
      if (resourceType === 'SHARED_RESOURCE') {
        if (eventType === 'RESOURCE_SHOW' && resource) {
          dispatch(applySharedResourceLiveEvent({ type: 'SHARED_RESOURCE_SHOW', resource }));
        } else if (eventType === 'RESOURCE_HIDE') {
          dispatch(
            applySharedResourceLiveEvent({
              type: 'SHARED_RESOURCE_HIDE',
              resource: { id: resourceId },
            }),
          );
        }
        return;
      }

      if (eventType === 'RESOURCE_SHOW') {
        if (webinarId && sessionIdentifier && authorization) {
          scheduleResourceStateRefetch(webinarId, sessionIdentifier, authorization);
        }

        if (resourceType === 'OFFER_TEMPLATE') {
          dispatch(
            fetchPublicOffer({
              webinarId,
              offerId: resourceId,
              authorization,
            }),
          );

          if (webinarId && sessionIdentifier && authorization) {
            dispatch(
              postAnalyticsEvent({
                webinarId,
                sessionIdentifier,
                authorization,
                input: {
                  eventType: 'OFFER_SEEN',
                  payloadJson: JSON.stringify({
                    offerId: resourceId,
                  }),
                  occurredAt: new Date().toISOString(),
                },
              }),
            );
          }
        }
      } else if (eventType === 'RESOURCE_HIDE') {
        if (webinarId && sessionIdentifier && authorization) {
          scheduleResourceStateRefetch(webinarId, sessionIdentifier, authorization);
        }

        if (resourceType === 'OFFER_TEMPLATE') {
          dispatch(clearPublicOffer());
        } else if (resourceType === 'SURVEY_TEMPLATE') {
          dispatch(clearPublicSurvey());
        } else if (resourceType === 'QUIZ_TEMPLATE') {
          dispatch(clearPublicQuiz());
        }
      }
    };

    const label = `${eventType} ${resourceType} ${resourceId}`;
    setTimeBasedEvent((prev) => [
      ...prev,
      { time: timeNow, action: () => runEventAction(), label },
    ]);
  };

  const handleSurveyResultsEvent = ({
    eventType,
    templateId,
    timeNow,
    onUpdate,
  }: {
    eventType: 'SHOW_SURVEY_RESPONSE' | 'HIDE_SURVEY_RESPONSE';
    templateId: number;
    timeNow: number;
    onUpdate: (templateId: number, show: boolean) => void;
  }) => {
    console.log('handleSurveyResultsEvent', { eventType, templateId, timeNow });

    const runEventAction = () => {
      if (eventType === 'SHOW_SURVEY_RESPONSE') {
        onUpdate(templateId, true);
      } else if (eventType === 'HIDE_SURVEY_RESPONSE') {
        onUpdate(templateId, false);
      }
    };

    const label = `${eventType} survey ${templateId}`;
    setTimeBasedEvent((prev) => [
      ...prev,
      { time: timeNow, action: () => runEventAction(), label },
    ]);
  };

  const handleEndSessionEvent = ({
    timeNow,
    onNavigate,
  }: {
    timeNow: number;
    onNavigate: () => void;
  }) => {
    console.log('handleEndSessionEvent', { timeNow });

    const runEventAction = () => {
      onNavigate();
    };

    setTimeBasedEvent((prev) => [
      ...prev,
      { time: timeNow, action: () => runEventAction(), label: 'END_SESSION' },
    ]);
  };

  useImperativeHandle(ref, () => ({
    target,
    handleResourceEvent,
    handleSurveyResultsEvent,
    handleEndSessionEvent,
    getCurrentTimestamp: () => playbackTimestampRef.current,
  }));

  const publicVideoRef = useRef<VideoViewHandle | null>(null);
  const publicResourceIdRef = useRef<number | null>(null);
  publicResourceIdRef.current = publicResource?.id ?? null;

  const channelName = useChannelName();

  useChannel(channelName ?? '', (message) => {
    if (!channelName || !message) return;
    const data: any = message?.data;

    if (data && typeof data === 'object') {
      if (data.resourceType === 'WEBINAR_RESOURCE' && webinarId && token) {
        let timeNow = new Date().getTime();

        try {
          if (data.payloadJson) {
            let payload: any = data.payloadJson;
            if (typeof payload === 'string') {
              payload = JSON.parse(payload);
            }
            if (payload && typeof payload.timeNow === 'number') {
              timeNow = payload.timeNow;
            }
          }
        } catch (e) {
          console.error('Error parsing payloadJson for resource event:', e, data.payloadJson);
        }

        if (data.eventType === 'RESOURCE_SHOW') {
          const runResourceAction = () => {
            dispatch(
              fetchPublicResource({
                webinarId,
                resourceId: data.resourceId,
                authorization: token,
              }),
            );
          };

          setTimeBasedEvent((prev) => [
            ...prev,
            {
              time: timeNow,
              action: runResourceAction,
              label: `RESOURCE_SHOW resourceId ${data.resourceId}`,
            },
          ]);
        } else if (data.eventType === 'RESOURCE_HIDE') {
          const runResourceAction = () => {
            dispatch(clearPublicResource());
          };

          setTimeBasedEvent((prev) => [
            ...prev,
            { time: timeNow, action: runResourceAction, label: 'RESOURCE_HIDE' },
          ]);
        } else if (
          (data.eventType === 'VIDEO_PLAY' ||
            data.eventType === 'VIDEO_PAUSE' ||
            data.eventType === 'VIDEO_MUTE' ||
            data.eventType === 'VIDEO_UNMUTE' ||
            data.eventType === 'VIDEO_SEEK') &&
          (!publicResource || data.resourceId === publicResource.id)
        ) {
          const resourceId = data.resourceId;
          const eventType = data.eventType;
          const payloadJson = data.payloadJson;

          const runVideoAction = () => {
            if (publicResourceIdRef.current !== resourceId) return;
            if (eventType === 'VIDEO_PLAY') {
              publicVideoRef.current?.play();
            } else if (eventType === 'VIDEO_PAUSE') {
              publicVideoRef.current?.pause();
            } else if (eventType === 'VIDEO_MUTE') {
              publicVideoRef.current?.mute();
            } else if (eventType === 'VIDEO_UNMUTE') {
              publicVideoRef.current?.unmute();
            } else if (eventType === 'VIDEO_SEEK') {
              try {
                let payload: any = payloadJson;
                if (typeof payload === 'string') {
                  payload = JSON.parse(payload);
                }
                const time = typeof payload?.currentTime === 'number' ? payload.currentTime : null;
                if (time !== null) {
                  publicVideoRef.current?.seek(time);
                }
              } catch (e) {
                console.error('Error parsing VIDEO_SEEK payload', e, payloadJson);
              }
            }
          };

          setTimeBasedEvent((prev) => [
            ...prev,
            { time: timeNow, action: runVideoAction, label: `VIDEO_${eventType}` },
          ]);
        }
      }
    }
  });

  return (
    <div
      className="w-full max-h-full lg:h-full min-w-0 min-h-0 relative overflow-hidden rounded-none lg:rounded-2xl compact-landscape:h-full compact-landscape:rounded-2xl"
      style={{
        // Ratio fixe des 4 flux (compositor 16:9) : sans lui, le conteneur s'effondre à 0 px en
        // mobile (< lg) tant que la vidéo n'a pas chargé et l'overlay d'attente devient invisible.
        aspectRatio: 16 / 9,
        backgroundColor: backgroundColor ?? '#ffffff',
      }}
      onClick={() => {
        if (!canHoverRef.current) revealControls();
      }}
      onMouseEnter={() => {
        if (canHoverRef.current) showControlsOnHover();
      }}
      onMouseLeave={() => {
        if (canHoverRef.current) hideControlsOnHover();
      }}
    >
      {hlsSupported ? (
        <>
          {/* Boîte 16:9 calée sur le rectangle réel de l'image : en desktop le conteneur est plus haut
              que la vidéo, ce clip garantit que l'overscan rogne bien le bord de l'image (et pas le bas
              du conteneur), à toutes les largeurs. */}
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Deux boîtes : la première borne la zone utile, la seconde est dimensionnée par sa
                hauteur et porte donc toujours le 16:9, même quand la zone est plus large que le
                ratio (paysage). Sans elle l'overscan ci-dessous ne rognerait plus le bord de
                l'image mais le bord du conteneur. */}
            <div className="w-full max-h-full aspect-video flex items-center justify-center">
              <div className="relative h-full aspect-video overflow-hidden rounded-none lg:rounded-2xl">
                <video
                  muted={isMuted}
                  autoPlay
                  preload="auto"
                  ref={videoHlsRef}
                  // origin-top-left + scale : léger overscan qui pousse hors cadre le liseré noir
                  // droite/bas baké par l'egress, clippé par le overflow-hidden de la boîte 16:9.
                  className="absolute inset-0 w-full h-full object-contain origin-top-left scale-[1.005]"
                  playsInline
                  controls={false}
                  controlsList="nodownload nofullscreen noremoteplayback"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <track
                    kind="captions"
                    src={`${import.meta.env.BASE_URL}live-empty-captions.vtt`}
                    srcLang={getAppLanguage(lng).code}
                    label={t('liveSettings.liveStreamNoCaptionsTrack')}
                  />
                </video>
              </div>
            </div>
          </div>
          {showControls && liveStreamManifestReady && !interactionVisible && (
            <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
              {/* Calque calé sur le rectangle 16:9 réel de la vidéo (object-contain) : en desktop le
                  conteneur est plus haut que l'image, ce calque évite que les contrôles tombent dans
                  la bande blanche du bas. */}
              <div className="w-full max-h-full aspect-video flex items-center justify-center">
                <div className="relative h-full aspect-video">
                  <div className="absolute bottom-3 right-3 flex items-center gap-2 pointer-events-auto">
                    {canUsePip && (
                      <button
                        type="button"
                        onClick={togglePictureInPicture}
                        aria-label={t('liveSettings.pictureInPicture')}
                        title={t('liveSettings.pictureInPicture')}
                        aria-pressed={isInPip}
                        className="flex items-center justify-center rounded-full bg-black/55 p-2 backdrop-blur-sm transition-colors hover:bg-black/75 cursor-pointer"
                      >
                        <PictureInPicture2 className="size-5 text-white" aria-hidden />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={enterFullscreen}
                      aria-label={t('liveSettings.fullscreen')}
                      title={t('liveSettings.fullscreen')}
                      className="flex items-center justify-center rounded-full bg-black/55 p-2 backdrop-blur-sm transition-colors hover:bg-black/75 cursor-pointer"
                    >
                      <Maximize2 className="size-5 text-white" aria-hidden />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!liveStreamManifestReady && (
            <div
              className="absolute inset-0 z-20 overflow-hidden rounded-none lg:rounded-2xl pointer-events-none"
              style={{ backgroundColor: visualIdentity?.theme === 'DARK' ? '#1F1F1F' : '#EEEAE5' }}
            >
              <img
                src={`${import.meta.env.BASE_URL}assets/welya-watermark.svg`}
                alt=""
                aria-hidden
                className={`absolute inset-x-0 bottom-0 top-[12%] w-full scale-90 object-cover ${
                  visualIdentity?.theme === 'DARK' ? 'invert' : ''
                }`}
              />
              {hasStreamPlayedRef.current ? (
                // Coupure en cours de live : même carte, même position centrée que celle bakée par
                // le compositor, pour une transition invisible quand l'egress meurt.
                <div className="absolute inset-0 flex items-center justify-center px-6">
                  <div className="flex max-w-lg items-start gap-3 rounded-xl bg-white p-6 shadow-[0px_4px_6px_0px_rgba(28,43,53,0.08)]">
                    <Hourglass className="size-7 shrink-0 text-[#2C6CFF]" aria-hidden />
                    <div className="flex flex-col gap-1">
                      <p className="text-xl font-medium leading-7 text-[#2C6CFF]">
                        {t('liveSettings.liveStreamInterruptedTitle')}
                      </p>
                      <p className="text-base leading-6 text-[#0C0E21]">
                        {t('liveSettings.liveStreamInterruptedBody')}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="absolute inset-x-0 top-6 flex justify-center px-6">
                  <div className="flex items-center gap-2 rounded-xl bg-white px-4 py-3 shadow-[0px_4px_6px_0px_rgba(28,43,53,0.08)]">
                    <Hourglass className="size-5 shrink-0 text-[#2C6CFF]" aria-hidden />
                    <p className="text-sm font-medium text-[#0C0E21] sm:text-base">
                      {t('liveSettings.liveWillStartSoon')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          {isMuted && hasPlaybackUrlForTarget && liveStreamManifestReady && (
            <button
              onClick={toggleMute}
              className="absolute hover:bg-black/80 cursor-pointer flex-col gap-6 left-1/2 flex flex-wrap items-center justify-center -translate-x-1/2 z-10 top-1/2 -translate-y-1/2 w-full  md:w-xl h-full lg:h-80 shadow-2xl backdrop-blur-sm overflow-x-auto bg-black/70 rounded-3xl p-2 md:p-8"
            >
              <div
                className="flex items-center justify-center p-3 rounded-xl"
                style={{
                  backgroundColor: visualIdentity?.secondaryBackgroundColor
                    ? `${visualIdentity.secondaryBackgroundColor}20`
                    : '#ffffff',
                }}
              >
                <Volume2
                  className="size-9"
                  style={{ color: visualIdentity?.secondaryBackgroundColor ?? '#ffffff' }}
                />
              </div>
              <p className="text-white text-xl md:text-3xl font-bold wrap-break-word w-full">
                {tWebinars('live.youtube.unmute.title')}
              </p>
              <p className="text-white text-xs md:text-sm font-normal wrap-break-word w-full">
                {tWebinars('live.youtube.unmute.subtitle')}
              </p>
            </button>
          )}
        </>
      ) : (
        <div className="text-red-500 text-center p-6 bg-gray-50 rounded">
          {tWebinars('live.hlsNotSupported')}
        </div>
      )}
    </div>
  );
});
